<template>
  <div class="data-safe-sdk-page">
    <div class="container">
      <header class="doc-header">
        <h1>数据安全SDK接口文档</h1>
        <p>提供统一返回格式、密钥管理与文件资产管理接口说明</p>
      </header>

      <div class="toc">
        <h2>目录</h2>
        <ul>
          <li v-for="item in toc" :key="item.id">
            <a
              :href="`#${item.id}`"
              :class="{ active: activeSectionId === item.id }"
              @click.prevent="scrollTo(item.id)"
            >
              {{ item.title }}
            </a>
          </li>
        </ul>
      </div>

      <section
        v-for="section in sections"
        :key="section.id"
        :id="section.id"
        class="api-section"
      >
        <h2 class="section-title">{{ section.title }}</h2>

        <div v-if="section.kind === 'response-format'" class="response-format">
          <h4>ResultCode 统一返回格式</h4>
          <p>所有接口均使用 ResultCode 统一返回格式，包含以下字段：</p>
          <ul>
            <li><strong>code</strong>：响应状态码，"200"表示成功，"9999"表示失败</li>
            <li><strong>msg</strong>：响应消息，"success"表示成功，失败时包含错误信息</li>
            <li><strong>flag</strong>：操作标志，true表示成功，false表示失败</li>
            <li><strong>data</strong>：响应数据，包含具体的业务数据</li>
          </ul>

          <div class="example-box">
            <div class="example-title">成功响应示例</div>
            <div class="example-code">
              <div class="copy-btn" @click="copyText(section.successExample)">
                {{ copiedText === section.successExample ? "已复制" : "复制" }}
              </div>
              <pre>{{ section.successExample }}</pre>
            </div>
          </div>

          <div class="example-box">
            <div class="example-title">失败响应示例</div>
            <div class="example-code">
              <div class="copy-btn" @click="copyText(section.failExample)">
                {{ copiedText === section.failExample ? "已复制" : "复制" }}
              </div>
              <pre>{{ section.failExample }}</pre>
            </div>
          </div>
        </div>

        <div v-else>
          <div v-for="api in section.apis" :key="api.key" class="api-item">
            <div class="api-path">
              <span class="api-method" :class="api.methodClass">
                {{ api.method }}
              </span>
              {{ api.path }}
            </div>
            <div class="api-description">{{ api.description }}</div>

            <h4>请求参数</h4>
            <table class="param-table">
              <thead>
                <tr>
                  <th>参数名</th>
                  <th>类型</th>
                  <th>是否必须</th>
                  <th>描述</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in api.params" :key="p.name">
                  <td>{{ p.name }}</td>
                  <td>
                    <span class="param-type">{{ p.type }}</span>
                  </td>
                  <td>
                    <span :class="p.required ? 'required' : 'optional'">
                      {{ p.required ? "是" : "否" }}
                    </span>
                  </td>
                  <td>{{ p.desc }}</td>
                </tr>
              </tbody>
            </table>

            <div class="example-box" v-if="api.requestExamples && api.requestExamples.length">
              <div class="example-title">
                请求示例
              </div>
              <div v-for="ex in api.requestExamples" :key="ex.title" class="example-block">
                <div v-if="api.requestExamples.length > 1" class="example-subtitle">
                  {{ ex.title }}
                </div>
                <div class="example-code">
                  <div class="copy-btn" @click="copyText(ex.code)">
                    {{ copiedText === ex.code ? "已复制" : "复制" }}
                  </div>
                  <pre>{{ ex.code }}</pre>
                </div>
              </div>
            </div>

            <div
              v-for="res in api.responseExamples"
              :key="res.title"
              class="example-box response-example"
            >
              <div class="example-title">{{ res.title }}</div>
              <div class="example-code">
                <div class="copy-btn" @click="copyText(res.code)">
                  {{ copiedText === res.code ? "已复制" : "复制" }}
                </div>
                <pre>{{ res.code }}</pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="footer">
        <p>© {{ new Date().getFullYear() }} 数据安全SDK接口文档</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DataSafeSdk",
  data() {
    return {
      toc: [
        { id: "response-format", title: "统一返回格式说明" },
        { id: "key-management", title: "密钥管理" },
        { id: "file-asset-management", title: "文件资产管理" },
      ],
      sections: [
        {
          id: "response-format",
          title: "统一返回格式说明",
          kind: "response-format",
          successExample:
            '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": {\n    // 具体的业务数据\n  }\n}',
          failExample:
            '{\n  "code": "9999",\n  "msg": "错误信息",\n  "flag": false,\n  "data": null\n}',
        },
        {
          id: "key-management",
          title: "密钥管理",
          kind: "group",
          apis: [
            {
              key: "key-generate",
              method: "POST",
              methodClass: "post",
              path: "/key/generate",
              description:
                "生成并保存密钥。根据文件ID和用户ID生成密钥，如果已存在则返回现有密钥。",
              params: [
                {
                  name: "fileId",
                  type: "String",
                  required: true,
                  desc: "文件ID，用于生成密钥的唯一标识之一",
                },
                {
                  name: "userId",
                  type: "String",
                  required: true,
                  desc: "用户ID，用于生成密钥的唯一标识之一",
                },
                {
                  name: "secretKey",
                  type: "String",
                  required: true,
                  desc: "密钥内容 限制长度为16位",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例",
                  code:
                    "POST /key/generate\nContent-Type: application/json\n\n{\n  \"fileId\": \"file-001\",\n  \"userId\": \"user-001\",\n  \"secretKey\": \"secret-key-123\"\n}",
                },
              ],
              responseExamples: [
                {
                  title: "响应示例",
                  code:
                    '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": true\n}',
                },
              ],
            },
            {
              key: "key-delete",
              method: "POST",
              methodClass: "post",
              path: "/key/delete",
              description: "删除密钥（逻辑删除）。根据文件ID删除对应的密钥。",
              params: [
                {
                  name: "fileId",
                  type: "String",
                  required: true,
                  desc: "文件ID，用于标识要删除的密钥",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例",
                  code:
                    "POST /key/delete\nContent-Type: application/json\n\n{\n  \"fileId\": \"file-001\"\n}",
                },
              ],
              responseExamples: [
                {
                  title: "成功响应示例",
                  code:
                    '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": true\n}',
                },
                {
                  title: "失败响应示例",
                  code:
                    '{\n  "code": "9999",\n  "msg": "删除密钥失败",\n  "flag": false,\n  "data": false\n}',
                },
              ],
            },
            {
              key: "key-query",
              method: "POST",
              methodClass: "post",
              path: "/key/query",
              description:
                "查询密钥。通过文件ID查询对应的密钥信息（仅查询未逻辑删除的记录）。",
              params: [
                {
                  name: "fileId",
                  type: "String",
                  required: true,
                  desc: "文件ID，用于标识要查询的密钥",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例",
                  code:
                    "POST /key/query\nContent-Type: application/json\n\n{\n  \"fileId\": \"file-001\"\n}",
                },
              ],
              responseExamples: [
                {
                  title: "成功响应示例",
                  code:
                    '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": {\n    "fileId": "file-001",\n    "userId": "user-001",\n    "secretKey": "secret-key-12345",\n    "createTime": "2023-12-15T10:30:00"\n  }\n}',
                },
                {
                  title: "失败响应示例（密钥不存在）",
                  code:
                    '{\n  "code": "9999",\n  "msg": "查询的密钥不存在",\n  "flag": false,\n  "data": null\n}',
                },
              ],
            },
          ],
        },
        {
          id: "file-asset-management",
          title: "文件资产管理",
          kind: "group",
          apis: [
            {
              key: "file-asset-add",
              method: "POST",
              methodClass: "post",
              path: "/file/asset",
              description:
                "添加文件资产。创建新的文件资产记录，支持设置父级文件ID以构建文件树结构。",
              params: [
                {
                  name: "fileId",
                  type: "String",
                  required: true,
                  desc: "文件ID，用于唯一标识文件",
                },
                {
                  name: "fileName",
                  type: "String",
                  required: false,
                  desc: "文件名称",
                },
                {
                  name: "fileFormat",
                  type: "String",
                  required: false,
                  desc: "文件格式/扩展名",
                },
                {
                  name: "fileSize",
                  type: "Long",
                  required: false,
                  desc: "文件大小（字节）",
                },
                {
                  name: "filePath",
                  type: "String",
                  required: false,
                  desc: "文件路径",
                },
                {
                  name: "description",
                  type: "String",
                  required: false,
                  desc: "文件描述",
                },
                {
                  name: "parentFileId",
                  type: "String",
                  required: false,
                  desc: "父级文件ID，用于构建文件树结构，必须为已存在的文件ID",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例（无父级文件）",
                  code:
                    "POST /file/asset\nContent-Type: application/json\n\n{\n  \"fileId\": \"file-001\",\n  \"fileName\": \"document.pdf\",\n  \"fileFormat\": \"pdf\",\n  \"fileSize\": 1048576,\n  \"filePath\": \"/uploads/documents/document.pdf\",\n  \"description\": \"重要文档\"\n}",
                },
                {
                  title: "请求示例（带父级文件）",
                  code:
                    "POST /file/asset\nContent-Type: application/json\n\n{\n  \"fileId\": \"file-002\",\n  \"fileName\": \"child.pdf\",\n  \"fileFormat\": \"pdf\",\n  \"fileSize\": 512000,\n  \"filePath\": \"/uploads/documents/child.pdf\",\n  \"description\": \"子文件\",\n  \"parentFileId\": \"file-001\"\n}",
                },
              ],
              responseExamples: [
                {
                  title: "响应示例",
                  code:
                    '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": {\n    "id": 1,\n    "fileId": "file-001",\n    "keyId": 1,\n    "fileName": "document.pdf",\n    "fileFormat": "pdf",\n    "fileSize": 1048576,\n    "filePath": "/uploads/documents/document.pdf",\n    "description": "重要文档",\n    "parentFileId": null,\n    "createTime": "2023-12-15T11:00:00",\n    "updateTime": "2023-12-15T11:00:00"\n  }\n}',
                },
              ],
            },
            {
              key: "file-asset-delete",
              method: "POST",
              methodClass: "post",
              path: "/file/asset/delete",
              description: "删除文件资产（逻辑删除）。",
              params: [
                {
                  name: "id",
                  type: "Long",
                  required: true,
                  desc: "文件资产ID",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例",
                  code:
                    "POST /file/asset/delete\nContent-Type: application/json\n\n{\n  \"id\": 1\n}",
                },
              ],
              responseExamples: [
                {
                  title: "成功响应示例",
                  code:
                    '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": true\n}',
                },
                {
                  title: "失败响应示例",
                  code:
                    '{\n  "code": "9999",\n  "msg": "删除文件资产失败",\n  "flag": false,\n  "data": false\n}',
                },
              ],
            },
            {
              key: "file-asset-delete-tree",
              method: "POST",
              methodClass: "post",
              path: "/file/asset/deleteTree",
              description:
                "树删除文件资产（递归删除）。删除指定文件资产及其所有子文件资产，支持多级文件树的递归删除。",
              params: [
                {
                  name: "id",
                  type: "Long",
                  required: true,
                  desc: "文件资产ID（根节点ID）",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例",
                  code:
                    "POST /file/asset/deleteTree\nContent-Type: application/json\n\n{\n  \"id\": 1\n}",
                },
              ],
              responseExamples: [
                {
                  title: "成功响应示例（删除了3个文件）",
                  code:
                    '{\n  "code": "200",\n  "msg": "删除成功，共删除3个文件资产",\n  "flag": true,\n  "data": 3\n}',
                },
                {
                  title: "失败响应示例",
                  code:
                    '{\n  "code": "9999",\n  "msg": "删除文件资产树失败",\n  "flag": false,\n  "data": 0\n}',
                },
              ],
            },
            {
              key: "file-asset-query-by-id",
              method: "POST",
              methodClass: "post",
              path: "/file/asset/queryById",
              description: "根据ID查询文件资产。",
              params: [
                {
                  name: "id",
                  type: "Long",
                  required: true,
                  desc: "文件资产ID",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例",
                  code:
                    "POST /file/asset/queryById\nContent-Type: application/json\n\n{\n  \"id\": 1\n}",
                },
              ],
              responseExamples: [
                {
                  title: "成功响应示例",
                  code:
                    '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": {\n    "id": 1,\n    "fileId": "file-001",\n    "keyId": 1,\n    "fileName": "document.pdf",\n    "fileFormat": "pdf",\n    "fileSize": 1048576,\n    "filePath": "/uploads/documents/document.pdf",\n    "description": "重要文档",\n    "parentFileId": null,\n    "createTime": "2023-12-15T11:00:00",\n    "updateTime": "2023-12-15T11:00:00"\n  }\n}',
                },
                {
                  title: "失败响应示例（文件资产不存在）",
                  code:
                    '{\n  "code": "9999",\n  "msg": "查询的文件资产不存在",\n  "flag": false,\n  "data": null\n}',
                },
              ],
            },
            {
              key: "file-asset-query-by-file-id",
              method: "POST",
              methodClass: "post",
              path: "/file/asset/queryByFileId",
              description: "根据文件ID查询文件资产。",
              params: [
                {
                  name: "fileId",
                  type: "String",
                  required: true,
                  desc: "文件ID",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例",
                  code:
                    "POST /file/asset/queryByFileId\nContent-Type: application/json\n\n{\n  \"fileId\": \"file-001\"\n}",
                },
              ],
              responseExamples: [
                {
                  title: "成功响应示例",
                  code:
                    '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": {\n    "id": 1,\n    "fileId": "file-001",\n    "keyId": 1,\n    "fileName": "document.pdf",\n    "fileFormat": "pdf",\n    "fileSize": 1048576,\n    "filePath": "/uploads/documents/document.pdf",\n    "description": "重要文档",\n    "parentFileId": null,\n    "createTime": "2023-12-15T11:00:00",\n    "updateTime": "2023-12-15T11:00:00"\n  }\n}',
                },
                {
                  title: "失败响应示例（文件资产不存在）",
                  code:
                    '{\n  "code": "9999",\n  "msg": "查询的文件资产不存在",\n  "flag": false,\n  "data": null\n}',
                },
              ],
            },
            {
              key: "file-asset-page",
              method: "POST",
              methodClass: "post",
              path: "/file/asset/page",
              description: "分页查询文件资产。支持按ID、文件ID、文件名和文件格式进行条件查询。",
              params: [
                {
                  name: "pageNum",
                  type: "int",
                  required: true,
                  desc: "页码，默认为1",
                },
                {
                  name: "pageSize",
                  type: "int",
                  required: true,
                  desc: "每页大小，默认为10",
                },
                {
                  name: "id",
                  type: "Long",
                  required: false,
                  desc: "文件ID",
                },
                {
                  name: "fileId",
                  type: "String",
                  required: false,
                  desc: "文件ID（模糊查询）",
                },
                {
                  name: "fileName",
                  type: "String",
                  required: false,
                  desc: "文件名（模糊查询）",
                },
                {
                  name: "fileFormat",
                  type: "String",
                  required: false,
                  desc: "文件格式（模糊查询）",
                },
              ],
              requestExamples: [
                {
                  title: "请求示例",
                  code:
                    "POST /file/asset/page\nContent-Type: application/json\n\n{\n  \"pageNum\": 1,\n  \"pageSize\": 10,\n  \"fileName\": \"document\"\n}",
                },
              ],
              responseExamples: [
                {
                  title: "响应示例",
                  code:
                    '{\n  "code": "200",\n  "msg": "success",\n  "flag": true,\n  "data": {\n    "records": [\n      {\n        "id": 1,\n        "fileId": "file-001",\n        "keyId": 1,\n        "fileName": "document.pdf",\n        "fileFormat": "pdf",\n        "fileSize": 1048576,\n        "filePath": "/uploads/documents/document.pdf",\n        "description": "重要文档",\n        "parentFileId": null,\n        "createTime": "2023-12-15T11:00:00",\n        "updateTime": "2023-12-15T11:00:00"\n      }\n    ],\n    "total": 1,\n    "size": 10,\n    "current": 1,\n    "pages": 1\n  }\n}',
                },
              ],
            },
          ],
        },
      ],
      activeSectionId: "response-format",
      copiedText: "",
    };
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    this.$nextTick(() => {
      this.handleScroll();
    });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        this.activeSectionId = id;
      }
    },
    handleScroll() {
      const sections = document.querySelectorAll(".api-section[id]");
      let current = "";
      const offset = 120;
      const scrollPos = window.pageYOffset || document.documentElement.scrollTop || 0;
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const top = rect.top + scrollPos;
        if (scrollPos >= top - offset) {
          current = section.getAttribute("id");
        }
      });
      if (current) {
        this.activeSectionId = current;
      }
    },
    copyText(text) {
      if (!text) {
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            this.setCopied(text);
          })
          .catch(() => {});
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand("copy");
          this.setCopied(text);
        } catch (e) {
            console.error("复制失败", e);
        }
        document.body.removeChild(textarea);
      }
    },
    setCopied(text) {
      this.copiedText = text;
      setTimeout(() => {
        if (this.copiedText === text) {
          this.copiedText = "";
        }
      }, 2000);
    },
  },
};
</script>

<style lang="scss" scoped>
.data-safe-sdk-page {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 20px 0 40px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC",
    "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial,
    sans-serif;
  color: #333333;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.doc-header {
  background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
  color: #ffffff;
  padding: 30px 0;
  text-align: center;
  border-radius: 10px;
  margin-bottom: 30px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.doc-header h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
}

.doc-header p {
  font-size: 1.1rem;
  opacity: 0.9;
}

.toc {
  background: #ffffff;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.toc h2 {
  margin-bottom: 15px;
  color: #2c3e50;
}

.toc ul {
  list-style-type: none;
}

.toc li {
  margin-bottom: 10px;
}

.toc a {
  color: #3498db;
  text-decoration: none;
  transition: color 0.3s ease;
}

.toc a:hover {
  color: #2980b9;
  text-decoration: underline;
}

.toc a.active {
  font-weight: bold;
}

.api-section {
  background: #ffffff;
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.api-section:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #3498db;
}

.api-item {
  margin-bottom: 25px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #ffffff;
  transition: all 0.3s ease;
}

.api-item:hover {
  border-color: #3498db;
  box-shadow: 0 3px 10px rgba(52, 152, 219, 0.1);
}

.api-method {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 0.9rem;
  margin-right: 10px;
  text-transform: uppercase;
}

.api-method.get {
  background-color: #61affe;
  color: #ffffff;
}

.api-method.post {
  background-color: #49cc90;
  color: #ffffff;
}

.api-method.put {
  background-color: #fca130;
  color: #ffffff;
}

.api-method.delete {
  background-color: #f93e3e;
  color: #ffffff;
}

.api-path {
  font-family: "Courier New", Courier, monospace;
  font-weight: bold;
  color: #2c3e50;
  font-size: 1.1rem;
  margin-bottom: 10px;
}

.api-description {
  margin-bottom: 15px;
  color: #555555;
}

.param-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 15px;
}

.param-table th,
.param-table td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.param-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
}

.param-table tr:hover {
  background-color: #f8f9fa;
}

.param-type {
  font-family: "Courier New", Courier, monospace;
  background-color: #f1f1f1;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9rem;
}

.required {
  color: #e74c3c;
  font-weight: bold;
}

.optional {
  color: #27ae60;
}

.example-box {
  background-color: #f8f9fa;
  border-left: 4px solid #3498db;
  padding: 15px;
  margin-top: 15px;
  border-radius: 0 5px 5px 0;
}

.example-title {
  font-weight: bold;
  margin-bottom: 10px;
  color: #2c3e50;
}

.example-block + .example-block {
  margin-top: 12px;
}

.example-subtitle {
  font-size: 0.95rem;
  margin-bottom: 6px;
  color: #2c3e50;
}

.example-code {
  position: relative;
  font-family: "Courier New", Courier, monospace;
  background-color: #2c3e50;
  color: #f8f8f2;
  padding: 15px;
  border-radius: 5px;
  overflow-x: auto;
  white-space: pre-wrap;
}

.example-code pre {
  margin: 0;
}

.copy-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 60px;
  padding: 5px 0;
  background-color: #3498db;
  color: #ffffff;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
}

.copy-btn:hover {
  background-color: #2980b9;
}

.response-example {
  margin-top: 15px;
}

.response-format {
  background-color: #e8f4f8;
  border: 1px solid #bee5eb;
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 15px;
}

.response-format h4 {
  color: #0c5460;
  margin-bottom: 10px;
}

.response-format ul {
  padding-left: 22px;
  margin-top: 8px;
}

.response-format li + li {
  margin-top: 4px;
}

.footer {
  text-align: center;
  padding: 20px 0;
  color: #7f8c8d;
  margin-top: 10px;
}

@media (max-width: 768px) {
  .container {
    padding: 0 10px;
  }

  .doc-header h1 {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .param-table {
    font-size: 0.9rem;
  }

  .param-table th,
  .param-table td {
    padding: 8px;
  }
}
</style>

